import "#/commons/data/section/year-calendar.css";
import StationClient from "#/lib/StationClient";
import moment from 'moment';
import React from 'react';
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";

import DailyStatsClient from '#/lib/DailyStatsClient';

import Button from '@material-ui/core/Button';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { MEASUREMENT_CATEGORY_ICONS, MEASUREMENT_LABELS } from "#/lib/MeasurementCategory";
import MonthlyStatsTables from "./MonthlyStatsTables";
import YearMonthRangePicker from "./YearMonthRangePicker";

import BaseStatsPage from "../components/BaseStatsPage";

const ReactSwal = withReactContent(Swal);
const loadingSwal = Swal.mixin({
	allowOutsideClick: false,
	allowEscapeKey: false,
	didOpen: () => {
		Swal.showLoading();
	},
});

class MonthlyStatsPage extends BaseStatsPage {

	renderCalendarPicker() {
		return (
			<YearMonthRangePicker onRangeChange={(e) => this.onPickRange(e)} />
		);
	}

	createCSVFileName = () => {
		const { selectedStation, allStations, interval: { start, end }, selectedCategory } = this.state;
		return `${MEASUREMENT_LABELS[selectedCategory]}
			${allStations ? 'ALL' : selectedStation.label}
			${moment(new Date(start)).format("MMYYYY")}-${moment(new Date(end)).format("MMYYYY")}`;
	}

	// ─── MonthlyStats-specific methods ───────────────────────────────────────────

	onPickRange(e) {
		const start = moment()
			.year(e.startYear)
			.month(e.startMonth - 1)   // Month in moment is 0-indexed
			.startOf('month')           // Start of the month
			.valueOf();                 // Get epoch time in milliseconds

		const end = moment()
			.year(e.endYear)
			.month(e.endMonth - 1)     // Month in moment is 0-indexed
			.endOf('month')             // End of the month (last moment of the month)
			.valueOf();                 // Get epoch time in milliseconds

		this.setState({
			data: null,
			selectedRange: [start, end],
			interval: { start, end },
			showGisPicker: false
		}, () => {
			this.fetchDailyData();
		});
	}

	fetchDailyData() {
		const { selectedStation, allStations, interval, selectedCategory, stationMapByCode } = this.state;

		const onSuccess = (data) => {
			data = this.convertDailyDataToMonthlyData(data);
			this.addCalculatedFieldsToData(data, stationMapByCode);
			loadingSwal.close();
			this.setState({ data, showGisPicker: false, tableKey: (this.state.tableKey + 1) % 1000 });
		};
		const onError = () => {
			loadingSwal.close();
			ReactSwal.fire('Errore nel caricamento dati!', '', 'error');
		};

		if (!!selectedStation && !!interval && !!selectedCategory) {
			loadingSwal.fire('Caricamento dati...');
			DailyStatsClient.getDailyStats(onSuccess, onError, {
				start: interval.start.valueOf(),
				end: interval.end.valueOf(),
				category: selectedCategory,
				stationCode: selectedStation.value
			});
		} else if (!!allStations && !!interval && !!selectedCategory) {
			loadingSwal.fire('Caricamento dati...');
			DailyStatsClient.getDailyStats(onSuccess, onError, {
				start: interval.start.valueOf(),
				end: interval.end.valueOf(),
				category: selectedCategory
			});
		} else {
			this.setState({ data: null });
		}
	}

	transformData(data, aggregationMap) {
		const transformedData = data.reduce((result, entry) => {
			const { dailyStats } = entry;

			dailyStats.forEach(stat => {
				const { stationCode, year, month } = stat;
				const key = `${stationCode}-${year}-${month}`;

				if (!result[key]) {
					result[key] = {
						timestampKey: Date.UTC(year, month - 1, 15), // Use the 15th day of the month
						stationCode,
						year,
						month,
						aggregated: {}
					};
					for (const aggKey in aggregationMap) {
						result[key].aggregated[aggKey] = [];
					}
				}

				for (const aggKey in aggregationMap) {
					const value = stat[aggKey];
					if (value != null) {
						result[key].aggregated[aggKey].push(value);
					}
				}
			});

			return result;
		}, {});

		const monthlyStats = Object.values(transformedData).reduce((acc, stat) => {
			const aggregatedValues = {};

			for (const [aggKey, aggFunc] of Object.entries(aggregationMap)) {
				const values = stat.aggregated[aggKey];
				switch (aggFunc) {
					case 'sum': aggregatedValues[aggKey] = values.reduce((sum, val) => sum + val, 0); break;
					case 'min': aggregatedValues[aggKey] = Math.min(...values); break;
					case 'max': aggregatedValues[aggKey] = Math.max(...values); break;
					case 'avg': aggregatedValues[aggKey] = values.reduce((sum, val) => sum + val, 0) / values.length; break;
					default: throw new Error(`Invalid aggregation function: ${aggFunc}`);
				}
			}

			const existingEntry = acc.find(entry => entry.timestampKey === stat.timestampKey);
			if (existingEntry) {
				existingEntry.monthlyStats.push({ stationCode: stat.stationCode, year: stat.year, month: stat.month, ...aggregatedValues });
			} else {
				acc.push({
					timestampKey: stat.timestampKey,
					monthlyStats: [{ stationCode: stat.stationCode, year: stat.year, month: stat.month, ...aggregatedValues }]
				});
			}

			return acc;
		}, []);

		return monthlyStats;
	}

	convertDailyDataToMonthlyData(data) {
		let aggregationMap = {};
		if (this.state.selectedCategory === "P") {
			aggregationMap = { value: 'sum' };
		} else if (this.state.selectedCategory === "T") {
			aggregationMap = { minValue: 'min', maxValue: 'max', avgValue: 'avg' };
		} else if (this.state.selectedCategory === "I") {
			aggregationMap = { outflow: 'sum', maxFlow: 'max', avgFlow: 'avg', maxH: 'max' };
		}
		return this.transformData(data, aggregationMap);
	}

	addCalculatedFieldsToData(data, stationMapByCode) {
		data.forEach(row => {
			row.date = moment(new Date(row.timestampKey)).format("MM/YYYY");
			row.monthlyStats.forEach(innerRow => {
				innerRow.date = moment(new Date(row.timestampKey)).format("MM/YYYY");
				innerRow.stationName = stationMapByCode[innerRow.stationCode];
			});
		});
	}

	downloadDataPdf = () => {
		const { selectedStation, allStations, interval: { start, end } } = this.state;
		this.tableRef.table.download("pdf", "data.pdf", {
			orientation: "portrait",
			title: `Report statistiche mensili\nDal ${allStations ? 'ALL' : selectedStation.label}${moment(new Date(start)).format("MMYYYY")}-${moment(new Date(end)).format("MMYYYY")}`,
			autoTable: doc => {
				doc.autoTableSetDefaults({
					addPageContent: (data) => {
						let footerStr = String(doc.internal.getNumberOfPages());
						doc.setFontSize(10);
						doc.text(footerStr, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10);
						doc.setFontSize(14);
					}
				});
				return {
					styles: { fillColor: [214, 236, 237], minCellWidth: 20 },
					headStyles: { textColor: [0, 0, 0] },
					columnStyles: {
						0: { cellWidth: 60 },
						[this.tableRef.table.columnManager.columns.findIndex(col => col.field === 'validate')]: { cellWidth: 170 }
					},
					margin: { top: 70, bottom: 70 }
				};
			}
		});
	};

	buildTable() {
		if (!this.state.data) {
			return (
				<>
					<Skeleton height={"10vh"} duration={8} />
					<Skeleton height={"70vh"} duration={8} />
				</>
			);
		}

		let stationLabel = <>{MEASUREMENT_CATEGORY_ICONS[this.state.selectedCategory]} {MEASUREMENT_LABELS[this.state.selectedCategory]} {!!this.state.selectedStation.value && "  -  " + this.state.selectedStation.label}</>;
		let title = <h3>Statistiche mensili {stationLabel}</h3>;

		return (
			<>
				<div className="row">
					<div className=" col-4 ml-auto my-auto">
						{title}
					</div>
					<div className="col-2 d-flex justify-content-center m-auto">
						<Button startIcon={<FaFileCsv />} size="small" variant="contained" color="primary" onClick={() => this.downloadDataCsv()}>Download csv</Button>
					</div>
					<div className="col-2 d-flex justify-content-center m-auto">
						<Button startIcon={<FaFilePdf />} size="small" variant="contained" color="primary" onClick={() => this.downloadDataPdf()}>Download pdf</Button>
					</div>
				</div>
				<div style={{ width: "92vw" }}>
					<MonthlyStatsTables
						selectedCategory={this.state.selectedCategory}
						data={this.state.data}
						tableKey={this.state.tableKey}
						nested={this.state.allStations}
						tableRef={el => this.tableRef = el}
						downloadData={() => this.downloadDataCsv()}
						label={stationLabel}
					/>
				</div>
			</>
		);
	}
}

export default MonthlyStatsPage;
